import {
  Field,
  HStack,
  Text,
  FieldLabelProps,
  FieldHelperTextProps,
  NativeSelectFieldProps,
  FieldRootProps,
} from '@chakra-ui/react';
import { forwardRef, ReactNode } from 'react';
import fonts from 'src/theme/fonts';
import FieldErrorMessage from './FieldErrorMessage';
import { NativeSelectField, NativeSelectRoot } from '../ui/native-select';

interface DropdownProps
  extends
    Omit<FieldRootProps, 'onBlur' | 'onChange'>,
    Pick<NativeSelectFieldProps, 'value' | 'onBlur' | 'onChange' | 'children'> {
  label?: string;
  errorMessage?: string;
  message?: string;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  size?: 'small' | 'large';
  labelProps?: FieldLabelProps;
  helperProps?: FieldHelperTextProps;
  selectProps?: NativeSelectFieldProps;
  isOptional?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

type DropdownRef = HTMLSelectElement;
const Dropdown = forwardRef<DropdownRef, DropdownProps>(
  (
    {
      value,
      placeholder,
      label,
      disabled,
      message,
      errorMessage,
      invalid,
      children,
      size,
      onBlur,
      onChange,
      labelProps,
      helperProps,
      selectProps,
      isOptional,
      leftIcon,
      rightIcon,
      ...props
    },
    ref,
  ) => (
    <Field.Root invalid={invalid} disabled={disabled} {...props}>
      {label && (
        <Field.Label {...labelProps} display="flex">
          {label}
          {isOptional && (
            <Text ml="5px" fontFamily={fonts.bold}>
              (optional)
            </Text>
          )}
        </Field.Label>
      )}
      <HStack gap="8px" w="100%">
        {leftIcon}
        <NativeSelectRoot size={size}>
          <NativeSelectField
            ref={ref}
            placeholder={placeholder}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            aria-label={selectProps?.['aria-label'] || placeholder || label}
            {...selectProps}
          >
            {children}
          </NativeSelectField>
        </NativeSelectRoot>
        {rightIcon}
      </HStack>
      {message && <Field.HelperText {...helperProps}>{message}</Field.HelperText>}
      {errorMessage && <FieldErrorMessage>{errorMessage}</FieldErrorMessage>}
    </Field.Root>
  ),
);

export default Dropdown;
